import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * @module useInitialKeys
 * @description Custom hook for fetching initial keys used for question categorization in the Mental Health Portal application.
 * Automatically fetches data when the component mounts and manages loading and error states.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to fetch and manage the list of initial keys
 * @function useInitialKeys
 * @returns {Object} Object containing initialKeys array, loading state, and error state
 */
export function useInitialKeys() {
  /**
   * @state {Array} initialKeys - The list of fetched initial keys
   */
  const [initialKeys, setInitialKeys] = useState([]);

  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(true);

  /**
   * @state {string|null} error - Error message if the API request fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Effect hook to fetch initial keys when the component mounts
   * @effect
   */
  useEffect(() => {
    /**
     * Fetches initial keys from the API
     * @async
     * @function fetchInitialKeys
     * @returns {Promise<void>}
     */
    async function fetchInitialKeys() {
      try {
        const { data } = await api.get('initial-keys');
        setInitialKeys(data);
      } catch (err) {
        setError('Erro ao buscar chaves iniciais');
      } finally {
        setLoading(false);
      }
    }

    fetchInitialKeys();
  }, []);

  /**
   * @typedef {Object} UseInitialKeysReturn
   * @property {Array} initialKeys - The list of fetched initial keys
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string|null} error - Error message if the API request fails, null otherwise
   */

  /**
   * @returns {UseInitialKeysReturn} The hook's return values
   */
  return { initialKeys, loading, error };
}
